/**
 * Placeholder replacement for CMS copy.
 *
 * CMS tokens use the form `{{key}}` in plain strings and Contentful rich text.
 * In rich text, consecutive text nodes are scanned together so placeholders
 * split by inline formatting (e.g. bold) still resolve, while marks on
 * surrounding text are preserved.
 * Replacement data is always normalized to a flat key/value map before calling
 * `replaceInString` or `replaceInRichText`.
 * Numbers are formatted to locale string.
 *
 * Input shape (module-specific, e.g. Accident Atlas API):
 *   { replacements: [{ key, value, unit? }, ...] }
 *
 * - key: placeholder name without braces
 * - value: string | number (coerced via formatToString)
 * - unit: reserved for future use, currently ignored
 */
import { formatToString } from '@tools/formatToString'

const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

export type PlaceholderReplacement = {
  key: string
  value: string | number
  unit?: string
}

export type PlaceholderMap = Record<string, string | number>

export function replacementsToMap(
  replacements: PlaceholderReplacement[] = []
): PlaceholderMap {
  return Object.fromEntries(replacements.map(({ key, value }) => [key, value]))
}

function formatNumericPlaceholders(placeholders: PlaceholderMap): void {
  Object.entries(placeholders).forEach(([key, value]) => {
    if (typeof value === 'number' && String(value).includes('.')) {
      placeholders[key] = value.toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    }
  })
}

export function replaceInString(
  text: string | undefined,
  placeholders: PlaceholderMap
): string | undefined {
  if (!text) return text

  formatNumericPlaceholders(placeholders)

  return text.replace(PLACEHOLDER_PATTERN, (_, key: string) => {
    const value = placeholders[key]
    return value !== undefined ? formatToString(value) : `{{${key}}}`
  })
}

export function replaceInRichText(
  richtext: RvG.Contentful.BasicRichTextType | undefined,
  placeholders: PlaceholderMap
): RvG.Contentful.BasicRichTextType | undefined {
  if (!richtext?.raw) return richtext

  try {
    formatNumericPlaceholders(placeholders)
    const document = JSON.parse(richtext.raw)
    const replaced = replaceInContentfulNode(document, placeholders)
    return {
      ...richtext,
      raw: JSON.stringify(replaced),
    }
  } catch {
    return richtext
  }
}

type TextNode = Record<string, unknown> & {
  nodeType: 'text'
  value: string
  marks?: unknown[]
}

type NodeRange = {
  node: TextNode
  start: number
  end: number
}

function isTextNode(node: unknown): node is TextNode {
  return (
    !!node &&
    typeof node === 'object' &&
    (node as Record<string, unknown>).nodeType === 'text' &&
    typeof (node as Record<string, unknown>).value === 'string'
  )
}

function sameMarks(
  a: unknown[] | undefined,
  b: unknown[] | undefined
): boolean {
  return JSON.stringify(a ?? []) === JSON.stringify(b ?? [])
}

/**
 * Replaces placeholders across consecutive text nodes while preserving marks.
 * Placeholders split by Contentful inline formatting (e.g. bold on the key)
 * still resolve; surrounding formatting is kept.
 */
function replaceInConsecutiveTextNodes(
  textNodes: TextNode[],
  placeholders: PlaceholderMap
): TextNode[] {
  const ranges: NodeRange[] = []
  let offset = 0
  for (const node of textNodes) {
    ranges.push({ node, start: offset, end: offset + node.value.length })
    offset += node.value.length
  }

  const joined = textNodes.map((node) => node.value).join('')
  const pattern = new RegExp(PLACEHOLDER_PATTERN.source, 'g')

  type Segment =
    | { kind: 'text'; start: number; end: number }
    | { kind: 'placeholder'; start: number; end: number; value: string }

  const segments: Segment[] = []
  let cursor = 0
  let match: RegExpExecArray | null
  let replacedAny = false

  while ((match = pattern.exec(joined)) !== null) {
    const key = match[1]
    const replacement = placeholders[key]
    if (replacement === undefined) continue

    replacedAny = true
    if (match.index > cursor) {
      segments.push({ kind: 'text', start: cursor, end: match.index })
    }
    segments.push({
      kind: 'placeholder',
      start: match.index,
      end: match.index + match[0].length,
      value: formatToString(replacement),
    })
    cursor = match.index + match[0].length
  }

  if (!replacedAny) {
    return textNodes
  }

  if (cursor < joined.length) {
    segments.push({ kind: 'text', start: cursor, end: joined.length })
  }

  const result: TextNode[] = []

  const pushNode = (source: TextNode, value: string) => {
    if (!value) return
    const previous = result[result.length - 1]
    if (previous && sameMarks(previous.marks, source.marks)) {
      previous.value += value
      return
    }
    result.push({
      ...source,
      value,
      marks: source.marks ?? [],
    })
  }

  for (const segment of segments) {
    if (segment.kind === 'placeholder') {
      const overlapping = ranges.filter(
        (range) => segment.start < range.end && segment.end > range.start
      )
      const withMarks = overlapping.find(
        (range) =>
          Array.isArray(range.node.marks) && range.node.marks.length > 0
      )
      pushNode(
        withMarks?.node ?? overlapping[0]?.node ?? textNodes[0],
        segment.value
      )
      continue
    }

    for (const range of ranges) {
      const start = Math.max(segment.start, range.start)
      const end = Math.min(segment.end, range.end)
      if (start >= end) continue
      pushNode(range.node, joined.slice(start, end))
    }
  }

  return result
}

/**
 * Walks rich-text content arrays, grouping consecutive text nodes so
 * placeholders split across inline marks still resolve.
 */
function replaceInContentArray(
  content: unknown[],
  placeholders: PlaceholderMap
): unknown[] {
  const result: unknown[] = []
  let index = 0

  while (index < content.length) {
    const item = content[index]

    if (isTextNode(item)) {
      const textNodes: TextNode[] = []
      let scan = index

      while (scan < content.length && isTextNode(content[scan])) {
        textNodes.push(content[scan] as TextNode)
        scan++
      }

      result.push(...replaceInConsecutiveTextNodes(textNodes, placeholders))
      index = scan
      continue
    }

    result.push(replaceInContentfulNode(item, placeholders))
    index++
  }

  return result
}

function replaceInContentfulNode(
  node: unknown,
  placeholders: PlaceholderMap
): unknown {
  if (!node || typeof node !== 'object') return node

  if (Array.isArray(node)) {
    return node.map((item) => replaceInContentfulNode(item, placeholders))
  }

  const record = node as Record<string, unknown>

  if (record.nodeType === 'text' && typeof record.value === 'string') {
    return {
      ...record,
      value: replaceInString(record.value, placeholders),
    }
  }

  if (Array.isArray(record.content)) {
    return {
      ...record,
      content: replaceInContentArray(record.content, placeholders),
    }
  }

  return record
}
